/**
 * WebSocket — ILOVA DARAJASIDA, ekranda emas. docs/API.md §12.
 *
 * **Nega ekranda emas.** Avval `useRealtime` har ekranda alohida ulanardi
 * va shu sabab haqiqiy xato bor edi:
 *
 *     1. foydalanuvchi `POST /match/search` yuboradi
 *     2. server juftlikni topadi va `match.found` ni DARHOL chiqaradi
 *     3. klient endi `/search` ekraniga o'tadi va endi WS'ga ulanadi
 *     4. event allaqachon o'tib ketgan — Redis pub/sub uni saqlamaydi
 *
 * Ya'ni juftlikni YOPGAN odam o'zining `match.found` ini ko'rmasdi.
 * Ulanish ilova ochilishi bilan tiklanadi va qidiruv boshlanguncha
 * allaqachon tayyor turadi.
 *
 * WS FAQAT serverdan klientga (D-047) — bu yerdan hech qachon xabar
 * yuborilmaydi.
 */

import type { WsEvent } from '@voice/api-client';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { getAccessToken } from '../api/client';
import {
  RealtimeContext,
  type RealtimeListener,
  type RealtimeStatus,
  type RealtimeValue,
} from './context';

const MAX_ATTEMPTS = 3;
const BACKOFF_MS = [1_000, 3_000, 7_000];

function socketUrl(): string {
  const base = import.meta.env['VITE_API_URL'] ?? 'http://localhost:8000';
  const token = getAccessToken() ?? '';
  // Brauzer WS handshake'da sarlavha qo'ya olmaydi — token URL'da ketadi.
  // Shuning uchun query string HECH QAYERDA log'ga yozilmaydi (D-058).
  return `${base.replace(/^http/, 'ws')}/ws?token=${encodeURIComponent(token)}`;
}

export function RealtimeProvider({
  enabled,
  children,
}: {
  /** Auth tugagandan keyin yoqiladi — tokensiz ulanish 401 bilan yopiladi. */
  enabled: boolean;
  children: ReactNode;
}) {
  const [status, setStatus] = useState<RealtimeStatus>('connecting');
  const listeners = useRef(new Set<RealtimeListener>());

  useEffect(() => {
    if (!enabled) return;

    let socket: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let stopped = false;

    function connect(): void {
      if (stopped) return;
      setStatus('connecting');
      socket = new WebSocket(socketUrl());

      socket.onopen = () => {
        attempt = 0;
        setStatus('open');
      };

      socket.onmessage = (message) => {
        let event: WsEvent;
        try {
          event = JSON.parse(String(message.data)) as WsEvent;
        } catch {
          // Bitta buzuq xabar uchun klientni yiqitish juda qimmat.
          return;
        }
        for (const listener of listeners.current) listener(event);
      };

      socket.onclose = () => {
        if (stopped) return;
        // Jim uzilib qolish TAQIQLANADI (docs/API.md §12): uch urinishdan
        // keyin holat `error` ga o'tadi va ekran buni ko'rsatadi.
        if (attempt >= MAX_ATTEMPTS) {
          setStatus('error');
          return;
        }
        const delay = BACKOFF_MS[attempt] ?? 7_000;
        attempt += 1;
        timer = setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      socket?.close();
    };
  }, [enabled]);

  const value = useMemo<RealtimeValue>(
    () => ({
      status,
      subscribe: (listener: RealtimeListener) => {
        listeners.current.add(listener);
        return () => listeners.current.delete(listener);
      },
    }),
    [status],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}
